import React from 'react';
import PropTypes from 'prop-types';
import { Divider } from '@mui/material';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import Carousel from 'react-material-ui-carousel';
import {
    TransformComponent,
    TransformWrapper,
} from 'react-zoom-pan-pinch';
import Spinner from '../../../Spinner';
import TipsPanel from '../TipsPanel';

const MAX_STAGE_ZOOM_SCALE = 4;
const STAGE_ZOOM_STEP = 0.25;
const DOUBLE_CLICK_ZOOM_STEP = 0.7;

function preventImageDrag(event) {
    event.preventDefault();
}

function StageMediaImage({
    alt,
    imageKey,
    src,
}) {
    return (
        <div className="media-stage-image-shell">
            <TransformWrapper
                key={imageKey}
                initialScale={1}
                minScale={1}
                maxScale={MAX_STAGE_ZOOM_SCALE}
                limitToBounds
                centerOnInit
                centerZoomedOut
                smooth
                wheel={{
                    step: STAGE_ZOOM_STEP,
                }}
                panning={{
                    velocityDisabled: true,
                }}
                doubleClick={{
                    mode: 'toggle',
                    step: DOUBLE_CLICK_ZOOM_STEP,
                }}
            >
                <TransformComponent
                    wrapperClass="media-stage-transform-wrapper"
                    contentClass="media-stage-transform-content"
                    wrapperStyle={{
                        width: '100%',
                        height: '100%',
                    }}
                    contentStyle={{
                        width: '100%',
                        height: '100%',
                    }}
                >
                    <img
                        className="media-stage-image"
                        src={src}
                        alt={alt}
                        draggable={false}
                        onDragStart={preventImageDrag}
                    />
                </TransformComponent>
            </TransformWrapper>
        </div>
    );
}

StageMediaImage.propTypes = {
    alt: PropTypes.string.isRequired,
    imageKey: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
};

function MediaStage({
    activeScan,
    mediaScans,
    selectedIndex,
    setSelectedIndex,
    setShowTips,
    showTips,
    thumbnailStates,
}) {
    if (!activeScan) {
        return null;
    }

    const mediaTipsState = {
        items: [
            'Double click to toggle zoom',
            'Scroll wheel or trackpad to zoom in and out',
            'Click and drag to pan while zoomed in',
        ],
        onToggle: () => setShowTips(!showTips),
        showTips,
        title: 'Image controls',
    };

    return (
        <div className="media-stage-shell">
            <div className="media-stage-badge">
                {activeScan.label}
                {' '}
                <Divider className="divider" orientation="vertical" flexItem sx={{ my: '0px' }} />
                {' '}
                {activeScan.typeLabel}
            </div>

            <Carousel
                className="media-stage-carousel"
                index={selectedIndex}
                onChange={(nextIndex) => {
                    if (typeof nextIndex === 'number') {
                        setSelectedIndex(nextIndex);
                    }
                }}
                autoPlay={false}
                animation="fade"
                duration={250}
                swipe={false}
                indicators={false}
                cycleNavigation={false}
                navButtonsAlwaysInvisible
                fullHeightHover={false}
                navButtonsWrapperProps={{
                    style: {
                        display: 'none',
                    },
                }}
                height="100%"
            >
                {mediaScans.map((scan, index) => {
                    const thumbnailState = thumbnailStates[scan.id];
                    const isSelected = index === selectedIndex;

                    return (
                        <div key={scan.id} className="media-stage-slide">
                            <div className="media-stage-card">
                                {thumbnailState?.status === 'ready' ? (
                                    <StageMediaImage
                                        imageKey={`${scan.id}-${isSelected ? 'selected' : 'idle'}`}
                                        src={thumbnailState.src}
                                        alt={scan.label}
                                    />
                                ) : thumbnailState?.status === 'failed' ? (
                                    <div className="media-stage-placeholder">
                                        <BrokenImageOutlinedIcon className="media-stage-placeholder-icon" />
                                        <span>Unable to load thumbnail</span>
                                    </div>
                                ) : (
                                    <div className="media-stage-loading-state" role="status" aria-live="polite">
                                        <Spinner type="inline-bigger" />
                                        <span>Loading thumbnail...</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </Carousel>
            <TipsPanel className="media-tips-container no-offset" tips={mediaTipsState} />
        </div>
    );
}

MediaStage.propTypes = {
    activeScan: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        typeLabel: PropTypes.string.isRequired,
    }),
    mediaScans: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        }),
    ).isRequired,
    selectedIndex: PropTypes.number.isRequired,
    setSelectedIndex: PropTypes.func.isRequired,
    setShowTips: PropTypes.func.isRequired,
    showTips: PropTypes.bool.isRequired,
    thumbnailStates: PropTypes.objectOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            status: PropTypes.oneOf([
                'failed',
                'loading',
                'ready',
            ]).isRequired,
        }),
    ).isRequired,
};

MediaStage.defaultProps = {
    activeScan: null,
};

export default MediaStage;
